import { AppShell } from '@mantine/core';
import {
  useImpersonation,
  useMobileDetector,
  useRoles,
} from '@zorro/web/ui-utils';
import { ReactNode } from 'react';

import { useAppLayout } from './AppLayoutContext';
import { AppLayoutHeader } from './AppLayoutHeader/AppLayoutHeader';
import { DiscoveryShoppingProps } from './AppLayoutHeader/AppLayoutHeaderDiscoveryShopping';
import { AppLayoutMain } from './AppLayoutMain';
import classNames from './AppLayoutMain/AppLayoutMain.module.scss';
import { AppLayoutSidebar } from './AppLayoutSidebar';
import { AppLayoutSidebarItem } from './AppLayoutSidebarItem';
import { AppLayoutConstants, AppLayoutVariant } from './AppLayoutUtils';

type Props = {
  children: ReactNode;
  variant?: AppLayoutVariant;
  sidebar?: { isAlwaysOpen?: boolean } | boolean;
  subheader?: { height: string };
  subHeaderContent?: ReactNode;
  headerContent?: ReactNode;
  withSearch?: boolean;
  withDonElenaMenuItems?: boolean;
  isDon?: boolean;
  isShoppingFlow?: boolean;
  analyticsPayload?: object;
  // AppLayoutHeader needs to be refactored to be defined from outside of AppLayout to prevent the feature bloat, each page needs to define it's own header.
  // If each page would declare it's own Header this hack wouldn't need to exist.
  // https://linear.app/myzorro/issue/ENG-914/applayoutheader-refactor
  isDiscoveryShoppingPleaseRefactorAppLayoutHeader?: boolean;
  discoveryShoppingProps?: DiscoveryShoppingProps;
};

export function AppLayout({
  children,
  variant,
  subheader,
  subHeaderContent,
  withSearch = false,
  sidebar,
  headerContent,
  withDonElenaMenuItems = false,
  isDon = false,
  isShoppingFlow = false,
  analyticsPayload,
  isDiscoveryShoppingPleaseRefactorAppLayoutHeader = false,
  discoveryShoppingProps,
}: Props) {
  const { isImpersonated } = useImpersonation();
  const { isSidebarOpen } = useAppLayout();
  const { isDesktop } = useMobileDetector();
  const { isNonZorroEnrollmentAgent } = useRoles();
  const withSidebar = Boolean(sidebar);
  const isSidebarAlwaysOpen =
    typeof sidebar === 'object' ? sidebar?.isAlwaysOpen : false;

  const subheaderHeight = subheader?.height ?? '0px';
  const baseHeaderHeight = isDiscoveryShoppingPleaseRefactorAppLayoutHeader
    ? AppLayoutConstants.HEADER_HEIGHT_DISCOVERY_SHOPPING
    : AppLayoutConstants.HEADER_HEIGHT;
  const headerHeight = isImpersonated
    ? `calc(${baseHeaderHeight} + ${AppLayoutConstants.IMPERSONATION_BAR_HEIGHT})`
    : baseHeaderHeight;

  return (
    <AppShell
      withBorder={false}
      classNames={classNames}
      data-variant={variant}
      data-desktop={isDesktop}
      data-with-sidebar={withSidebar}
      data-with-subheader={Boolean(subheader)}
      style={{
        '--app-layout-subheader-height': subheaderHeight,
        '--app-layout-header-height': headerHeight,
      }}
      header={{ height: `calc(${headerHeight} + ${subheaderHeight})` }}
      navbar={{
        width:
          isSidebarOpen || isSidebarAlwaysOpen
            ? AppLayoutConstants.MENU_OPEN_WIDTH
            : AppLayoutConstants.MENU_CLOSE_WIDTH,
        collapsed: { mobile: true, desktop: !withSidebar },
        breakpoint: 'xs',
      }}
    >
      <AppLayoutHeader
        withMenuButton={withSidebar && !isSidebarAlwaysOpen}
        withDonElenaMenuItems={withDonElenaMenuItems}
        headerContent={headerContent}
        subHeaderContent={subHeaderContent}
        withSearch={withSearch && !isNonZorroEnrollmentAgent}
        isDesktop={isDesktop}
        isDon={isDon}
        analyticsPayload={analyticsPayload}
        isShoppingFlow={isShoppingFlow}
        isDiscoveryShoppingPleaseRefactorAppLayoutHeader={
          isDiscoveryShoppingPleaseRefactorAppLayoutHeader
        }
        discoveryShoppingProps={discoveryShoppingProps}
      />
      {children}
    </AppShell>
  );
}

AppLayout.SidebarItem = AppLayoutSidebarItem;
AppLayout.Sidebar = AppLayoutSidebar;
AppLayout.Main = AppLayoutMain;
